import React, { useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ClipboardList, ChevronDown, ChevronUp, Clock } from 'lucide-react';
import { useGameLoopContext, useGameDataContext } from '../app/GameProviders';
import { ASSETS } from '../assetManifest.js';
import { CONSTANTS } from '../data.js';
import type { Order } from '../types';

const OrderItem = React.memo(({ order }: { order: Order }) => {
  const { MATERIALS } = useGameDataContext();
  const [timeLeft, setTimeLeft] = useState<number>(0);

  useEffect(() => {
    if (!order.expiresAt) return;

    const updateTimer = () => {
      const now = Date.now();
      const remaining = Math.max(0, order.expiresAt! - now);
      setTimeLeft(remaining);
    };

    updateTimer();
    const interval = setInterval(updateTimer, 100);

    return () => clearInterval(interval);
  }, [order.expiresAt]);

  const material = MATERIALS[order.recipeId];
  if (!material) return null;

  const seconds = Math.ceil(timeLeft / 1000);
  const isUrgent = seconds <= 5;
  const isExpiring = seconds <= 10;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className={`rounded-lg p-2 text-sm flex items-center gap-3 border transition-all ${
        isUrgent 
          ? 'bg-red-500/30 border-red-500/50 animate-pulse' 
          : isExpiring 
          ? 'bg-orange-500/20 border-orange-500/40' 
          : 'bg-slate-700/50 border-slate-600/30'
      }`}
    >
      <span className="text-xl">{material.icon}</span>
      <div className="flex-1">
        <div className="font-medium text-cyan-200">{material.name}</div>
        {order.expiresAt && (
          <div className={`flex items-center gap-1 text-xs ${
            isUrgent ? 'text-red-300' : isExpiring ? 'text-orange-300' : 'text-slate-400'
          }`}>
            <Clock className="w-3 h-3" />
            <span>{seconds}s</span>
          </div>
        )}
      </div>
    </motion.div>
  );
});

export function OrderPanel() {
  const [isOpen, setIsOpen] = useState(false);
  const { orders } = useGameLoopContext();
  const [ordersButtonHovered, setOrdersButtonHovered] = useState(false);

  // Use the same visuals as pause/settings buttons for consistency
  const settingsButtonCfg = (ASSETS as any)?.ui?.settings_button;
  const settingsButtonHoverCfg = (ASSETS as any)?.ui?.settings_button_hover;

  if (!settingsButtonCfg || !settingsButtonCfg.path || !settingsButtonHoverCfg || !settingsButtonHoverCfg.path) {
    throw new Error('Settings button assets are missing or invalid in assetManifest.js');
  }

  return (
    <div className="fixed top-20 right-4 z-[1000]">
      <div className="flex flex-col items-end gap-2">
        <button
          onClick={() => setIsOpen(prev => !prev)}
          onMouseEnter={() => setOrdersButtonHovered(true)}
          onMouseLeave={() => setOrdersButtonHovered(false)}
          className="relative flex items-center justify-center transition-transform transform hover:scale-105"
          style={{
            width: `${settingsButtonCfg.width}px`,
            height: `${settingsButtonCfg.height}px`,
            backgroundImage: `url(${ordersButtonHovered ? settingsButtonHoverCfg.path : settingsButtonCfg.path})`,
            backgroundSize: '100% 100%',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
          }}
        >
          <span className="text-black font-medium text-lg drop-shadow flex items-center gap-2">
            <ClipboardList className="w-5 h-5" />
            Orders ({orders.length})
            {isOpen ? <ChevronDown className="w-5 h-5" /> : <ChevronUp className="w-5 h-5" />}
          </span>
        </button>

        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: 10, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.98 }}
              className="bg-slate-800/90 backdrop-blur rounded-xl p-4 w-[280px] shadow-lg border border-slate-700"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <ClipboardList className="w-5 h-5 text-amber-400" />
                  <span className="font-semibold">Customer Orders</span>
                </div>
              </div>
              <div className="text-xs text-slate-300 mb-3">
                Orders expire after {CONSTANTS.ORDER_SYSTEM.TIMEOUT_MS / 1000} seconds. Complete them quickly for rewards!
              </div>
              <div className="space-y-2 max-h-64 overflow-y-auto pr-2">
                <AnimatePresence>
                  {orders.length > 0 ? (
                    orders.map(order => <OrderItem key={order.id} order={order} />)
                  ) : (
                    <div className="text-center text-xs text-slate-400 italic py-4">
                      No open orders. A good time to stock up!
                    </div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
